import Configuration from "./config/configuration";
import { Dimensions, Platform } from "react-native";
import HmacSHA256 from "crypto-js/hmac-sha256";
import Base64 from "crypto-js/enc-base64";
import { Buffer } from "buffer";
import { schedule } from "./scheduler";
import AppConfig from "./config/AppConfig";
import { getSystemVersion, getDeviceName } from 'react-native-device-info';

if (global.Intl === undefined) {
  require("intl");
  require("intl/locale-data/jsonp/en.js");
}

class ApiService {
  constructor() {
    this.config = new Configuration();
    this.user = null;
    this.token = null;
    this.installId = null;
    this.lastBulkUpdateChecksums = null;
    this.isOffline = false;
    this.impersonate = null;
    this.confirmNonce = null;

    this.deviceInfo = { ver: null, name: null };
    if (Platform.OS !== "web") {
      const data = this.deviceInfo;
      data.ver = getSystemVersion();
      getDeviceName().then((deviceName) => {
        data.name = deviceName;
      });
    }
  }

  increaseConfirmNonce() {
    if (this.confirmNonce == null) {
      this.confirmNonce = new Date().getTime();
    }
    this.confirmNonce++;
  }

  setUser(user, token, installId) {
    this.user = user;
    this.token = token;
    this.installId = installId;
    if (this.user !== user || this.token !== token) {
      this.lastBulkUpdateChecksums = null;
    }
  }

  setImpersonate(user) {
    if (this.impersonate != user) {
      this.impersonate = user;
      this.lastBulkUpdateChecksums = null;
    }
  }

  getTime() {
    function pad2(n) {
      return (n < 10 ? "0" : "") + n;
    }
    const date = new Date();
    return (
      date.getFullYear() +
      pad2(date.getMonth() + 1) +
      pad2(date.getDate()) +
      pad2(date.getHours()) +
      pad2(date.getMinutes()) +
      pad2(date.getSeconds())
    );
  }

  getTZ() {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz !== undefined ? tz : new Date().getTimezoneOffset();
  }

  async retrieveBulkUpdates(
    upload_data,
    offlineActions,
    calendarItems,
    healthItems,
    selectedPage,
    cachedReferences,
  ) {
    return this.retrieveUpdates(
      "/bulk_updates",
      this._createBody({
        ver: AppConfig.DISPLAY_VERSION,
        checksums: this.lastBulkUpdateChecksums,
        confirmNonce: this.confirmNonce,
        data: upload_data,
        selectedPage: selectedPage,
        offlineActions: offlineActions,
        calendarItems: calendarItems,
        healthItems: healthItems,
        cachedRefs: cachedReferences,
      }),
    );
  }

  async uploadState(upload_data) {
    return this.retrieveItems(
      "/upload_state",
      this._createBody({
        data: upload_data,
      }),
    );
  }

  async pay(paymentData) {
    const url = "/pay";
    const body = this._createBody({
      data: paymentData,
    });

    console.log("Retrieving " + this.config.API_URL + url);

    const nonce = new Date().getTime();
    const hmac = this.sign(url, nonce, body);

    return fetch(this.config.API_URL + url, {
      method: "POST",
      body: body,
      headers: new Headers({
        "x-api-key": this.config.API_KEY,
        "x-nonce": nonce,
        "x-sig": hmac,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          this.handleResponseError(response);
        }
        return response.json();
      })
      .then((json) => {
        console.log("Retrieved pay result");
        console.log(json);

        return json;
      })
      .catch((error) => {
        this.handleError(error);
        return null;
      });
  }

  async deviceData(requestData) {
    const url = "/dev_data";
    const body = this._createBody({
      data: requestData,
    });

    console.log("Retrieving " + this.config.API_URL + url);

    const nonce = new Date().getTime();
    const hmac = this.sign(url, nonce, body);

    return fetch(this.config.API_URL + url, {
      method: "POST",
      body: body,
      headers: new Headers({
        "x-api-key": this.config.API_KEY,
        "x-nonce": nonce,
        "x-sig": hmac,
      }),
    })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        this.handleError(error);
        return null;
      });
  }

  sign(url, nonce, body) {
    const data =
      this.config.API_VERSION +
      url +
      "\n" +
      this.config.API_KEY +
      "\n" +
      nonce +
      "\n" +
      body;
    const hmac = HmacSHA256(
      data,
      new Buffer(this.config.SECRET, "base64").toString("ascii"),
    ).toString(Base64);
    return hmac;
  }

  getUrl() {
    return this.config.API_URL;
  }

  retrieveUpdates(url, body) {
    console.log(
      "retrieveUpdates() Retrieving " +
        this.config.API_URL +
        url +
        " with " +
        body &&
        body.length &&
        body.length > 1000
        ? body.substring(0, 1000)
        : body,
    );

    const nonce = new Date().getTime();
    const hmac = this.sign(url, nonce, body);

    return fetch(this.config.API_URL + url, {
      method: "POST",
      body: body,
      headers: new Headers({
        "x-api-key": this.config.API_KEY,
        "x-nonce": nonce,
        "x-sig": hmac,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          this.handleResponseError(response);
        }
        return response.json();
      })
      .then((json) => {
        //console.log("Retrieved bulk update");
        //console.log(json);
        // if (json.checksums != null) { //moved to controller, updating only when procesed
        //   this.lastBulkUpdateChecksums = json.checksums;
        // }
        return json;
      })
      .catch((error) => {
        this.handleError(error);
        return null;
      });
  }

  retrieveItems(url, body) {
    console.log("Retrieving " + this.config.API_URL + url);

    const nonce = new Date().getTime();
    const hmac = this.sign(url, nonce, body);

    return fetch(this.config.API_URL + url, {
      method: "POST",
      body: body,
      headers: new Headers({
        "x-api-key": this.config.API_KEY,
        "x-nonce": nonce,
        "x-sig": hmac,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          this.handleResponseError(response);
        }
        return response.json();
      })
      .then((json) => {
        console.log("Retrieved items");
        //console.log(json);
        const items = [];
        const itemArray = json;

        for (let i = 0; i < itemArray.length; i++) {
          items.push(itemArray[i]);
        }
        return items;
      })
      .catch((error) => {
        this.handleError(error);
        return null;
      });
  }

  async getItem(itemKey) {
    const url = "/mana";
    console.log("Retrieving " + this.config.API_URL + url);

    const body = this._createBody({
      id: itemKey,
    });
    const nonce = new Date().getTime();
    const hmac = this.sign(url, nonce, body);

    return fetch(this.config.API_URL + "/mana", {
      method: "POST",
      body: body,
      headers: new Headers({
        "x-api-key": this.config.API_KEY,
        "x-nonce": nonce,
        "x-sig": hmac,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          this.handleResponseError(response);
        }
        return response.json();
      })
      .then((item) => {
        return item;
      })
      .catch((error) => {
        this.handleError(error);
        return null;
      });
  }

  async cardAction(item, offlineActions) {
    console.log("ApiService.cardAction():");
    console.log(item);
    const url = "/card_action";

    const body = this._createBody({
      action: item,
      offlineActions: offlineActions,
    });
    const nonce = new Date().getTime();
    const hmac = this.sign(url, nonce, body);
    console.log(body);

    return fetch(this.config.API_URL + url, {
      method: "POST",
      body: body,
      headers: new Headers({
        "x-api-key": this.config.API_KEY,
        "x-nonce": nonce,
        "x-sig": hmac,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          this.handleResponseError(response);
        }
        return response.json();
      })
      .then((json) => {
        return json;
      })
      .catch((error) => {
        this.handleError(error);
        return null;
      });
  }

  async sendNotification(event, data) {
    console.log("ApiService.sendNotification():");
    const url = "/notif";

    const body = this._createBody({
      event: event,
      data: data,
    });
    const nonce = new Date().getTime();
    const hmac = this.sign(url, nonce, body);
    console.log(body);

    return fetch(this.config.API_URL + url, {
      method: "POST",
      body: body,
      headers: new Headers({
        "x-api-key": this.config.API_KEY,
        "x-nonce": nonce,
        "x-sig": hmac,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          this.handleResponseError(response);
        }
        return response.json();
      })
      .catch((error) => {
        this.handleError(error);
        return null;
      });
  }

  handleResponseError(response) {
    throw new Error("HTTP error, status = " + response.status);
  }

  retryOnline() {
    this.isOffline = false;
  }

  handleError(error) {
    //this.isOffline = true; //2020.10.06 - do not switch off the old way, to be removed with the new offline mode
    console.log(error);
  }

  /**
   * Serializes the payload data with the properties needed.
   * @private
   * @param {Object} data
   * @returns {String}
   */
  _createBody(data = {}) {
    return JSON.stringify({
      time: this.getTime(),
      tz: this.getTZ(),
      os: Platform.OS,
      osver: this.deviceInfo.ver,
      device: this.deviceInfo.name,
      browser:
        window !== undefined && window.navigator !== undefined
          ? window.navigator.userAgent
          : "",
      dx: Dimensions.get("window").width,
      dy: Dimensions.get("window").height,
      user: this.user,
      token: this.token,
      iid: this.installId,
      ver: AppConfig.DISPLAY_VERSION + "-" + AppConfig.UPDATE_VERSION,
      impersonate: this.impersonate,
      ...data,
    });
  }
}

export default ApiService;
